import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

/**
 * What a relayed ack was backed by (CAP-467).
 *
 * A crew that acks by tapback-liking a text has that ack recorded by someone
 * else, so the history would otherwise say only who relayed it. This shows
 * the crew's own reaction time, the screenshot, and - folded away - the hash
 * that ties the reaction to a specific message.
 */
export function AckEvidence({
  evidence,
  reactedLabel,
}: {
  evidence: {
    reacted_at: string
    message_sha256: string
    message_bytes: number
    chat_guid?: string | null
    proof_id?: string | null
  }
  /** Pre-formatted in the caller, which already holds the property's timezone. */
  reactedLabel: string
}) {
  return (
    <div className="mt-2 rounded-md border border-border bg-surfaceMuted/50 p-2">
      <p className="text-xs text-textMuted">Crew reacted {reactedLabel} · relayed afterwards</p>
      {evidence.proof_id && <AckProofImage proofId={evidence.proof_id} />}
      <details className="mt-1.5">
        <summary className="cursor-pointer text-xs text-textFaint">
          Which message they reacted to
        </summary>
        <p className="mt-1 break-all font-mono text-[11px] text-textFaint">
          sha256 {evidence.message_sha256}
          <br />
          {evidence.message_bytes} bytes
          {evidence.chat_guid && (
            <>
              <br />
              {evidence.chat_guid}
            </>
          )}
        </p>
      </details>
    </div>
  )
}

/**
 * The screenshot, fetched with the session rather than linked.
 *
 * The object URL is revoked when this unmounts: without that, every drawer
 * open would leak another copy of the image for the life of the tab.
 */
function AckProofImage({ proofId }: { proofId: string }) {
  const query = useQuery({
    queryKey: ['cleaning', 'ack-proof', proofId],
    queryFn: () => api.getCleaningAckProof(proofId),
    staleTime: Infinity,
  })

  // Derived in a memo rather than pushed into state from an effect: setting
  // state synchronously in an effect costs an extra render pass for a value
  // that is a pure function of the blob.
  const objectUrl = useMemo(
    () => (query.data ? URL.createObjectURL(query.data) : null),
    [query.data]
  )

  // Revoked on unmount and whenever the blob changes. Without this, every
  // drawer open leaks another copy of the image for the life of the tab.
  useEffect(() => {
    if (!objectUrl) return
    return () => URL.revokeObjectURL(objectUrl)
  }, [objectUrl])

  if (query.isError) {
    return <p className="mt-1.5 text-xs text-danger">Screenshot could not be loaded.</p>
  }
  if (!objectUrl) {
    return <div className="mt-1.5 h-24 w-40 animate-pulse rounded border border-border" />
  }
  return (
    <a href={objectUrl} target="_blank" rel="noreferrer" className="mt-1.5 inline-block">
      <img
        src={objectUrl}
        alt="Screenshot of the crew's reaction to the schedule message"
        className="max-h-40 rounded border border-border"
      />
    </a>
  )
}
